import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import { apiUrl } from '../../../../front/src/config';
import { TOKEN_NAME } from '../../../../front/src/constants/graphlogin';
import axiosInstance from '../../../../front/src/services/axios';
import type { AuthResponse } from '../../../../front/src/types/auth';
import type { GraphLoginAsync } from '../../components/auth/LoadGraphLogin';
import { getGraphLogin } from '../../components/auth/LoadGraphLogin';
import { JWTService } from '../jwt/JWTService';
import { clearNativeTokens, loadNativeTokens, saveNativeTokens } from './nativeTokenStorage';

// Keep track of the singleton registration so Capacitor listeners are only attached once.
let hasRegistered = false;
// Prevent concurrent syncs when the app toggles foreground quickly.
let pendingSync: Promise<void> | null = null;

// Default timeout for waiting for token sync (5 seconds)
const WAIT_FOR_SYNC_TIMEOUT_MS = 5000;

/**
 * Wait for the current token sync to complete.
 * Useful for components that need to ensure the token is refreshed before making requests.
 * Returns immediately if no sync is in progress.
 *
 * @param timeoutMs - Maximum time to wait for sync (default: 5000ms)
 */
export async function waitForTokenSync(
  timeoutMs: number = WAIT_FOR_SYNC_TIMEOUT_MS,
): Promise<void> {
  if (!pendingSync) {
    console.log('🔐 No pending sync, continuing immediately');
    return;
  }

  console.log('🔐 Waiting for token sync to complete...');
  const timeout = new Promise<void>(resolve =>
    setTimeout(() => {
      console.warn('⚠️ Token sync wait timed out after', timeoutMs, 'ms');
      resolve();
    }, timeoutMs),
  );

  await Promise.race([pendingSync, timeout]);
  console.log('🔐 Token sync wait completed');
}

// Timeout for waiting GraphLogin to be ready (10 seconds)
const GRAPHLOGIN_READY_TIMEOUT_MS = 10000;

/**
 * Wait for GraphLogin to be ready with a timeout to avoid waiting indefinitely.
 * Returns true if ready, false if timeout.
 */
async function waitForGraphLoginReady(
  graphLogin: GraphLoginAsync,
  timeoutMs: number = GRAPHLOGIN_READY_TIMEOUT_MS,
): Promise<boolean> {
  return new Promise<boolean>(resolve => {
    const timeoutId = setTimeout(() => {
      console.warn('⚠️ GraphLogin ready timeout after', timeoutMs, 'ms');
      resolve(false);
    }, timeoutMs);

    graphLogin.isReady
      .then(() => {
        clearTimeout(timeoutId);
        resolve(true);
      })
      .catch(error => {
        clearTimeout(timeoutId);
        console.error('⚠️ GraphLogin ready wait failed:', error);
        resolve(false);
      });
  });
}

/**
 * Check if a JWT token is expired by decoding it and comparing exp with current time.
 * Returns true if expired or if decoding fails.
 */
function isTokenExpired(token: string): boolean {
  try {
    const decoded = JWTService.decode<{ exp: number }>(token);
    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const isExpired = Date.now() >= expirationTime;
    if (isExpired) {
      console.log('🔐 Token is expired');
    }
    return isExpired;
  } catch (error) {
    console.warn('⚠️ Failed to decode token, considering it expired:', error);
    return true; // If we can't decode, consider it expired
  }
}

/**
 * Rehydrates GraphLogin whenever the native shell boots or resumes.
 * The goal is to refresh the access token *before* Next middleware inspects cookies.
 */
export function initNativeSessionManager(websiteKey?: string, locale?: string) {
  if (!websiteKey || !locale) return;
  if (!Capacitor.isNativePlatform()) return;
  if (hasRegistered) return;

  hasRegistered = true;

  const triggerSync = () => {
    if (pendingSync) return pendingSync;
    pendingSync = syncTokensWithGraphLogin(websiteKey, locale).finally(() => {
      pendingSync = null;
    });
    return pendingSync;
  };

  // Perform an initial sync when the app boots so the first request already has a valid token.
  triggerSync().catch(error => {
    console.error('🔐 Error in native session sync (boot):', error);
  });

  App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      // Each resume rehydrates the session and optionally refreshes the token.
      triggerSync().catch(error => {
        console.error('🔐 Error in native session sync (resume):', error);
      });
    }
  });
}

async function syncTokensWithGraphLogin(websiteKey: string, locale: string) {
  try {
    const storedTokens = await loadNativeTokens(websiteKey);
    if (!storedTokens || !storedTokens.refreshToken) {
      console.log('🔐 No native tokens or refresh token, skipping sync');
      return;
    }

    let newTokens: AuthResponse | null = null;
    const currentAccessToken = storedTokens.token;
    const isCurrentExpired = !currentAccessToken || isTokenExpired(currentAccessToken);

    // STEP 1: refresh via API before touching GraphLogin/cookies
    if (isCurrentExpired) {
      try {
        // Le drapeau `X-Native-App` part tout seul avec cette requête (posé sur
        // l'instance axios par `useCapacitor`) — cf. `services/jwt/JWTService.ts`
        // pour le pourquoi du retrait de `X-Native-Id`.
        const headers: Record<string, string> = { website: websiteKey };

        const { data } = await axiosInstance.post<AuthResponse>(
          `${apiUrl}/api/pub/refresh-token`,
          { refreshToken: storedTokens.refreshToken },
          { headers },
        );
        newTokens = data;
        await saveNativeTokens(websiteKey, newTokens);
        console.log('🔐 Token refreshed via API before cookie restore');
      } catch (error) {
        console.error('Failed to refresh token from native storage:', error);
        await clearNativeTokens(websiteKey);
        console.log('🔐 Cleared invalid tokens from Preferences');
        return;
      }
    }

    // STEP 2: restore cookie immediately for middleware
    const tokenToUse = newTokens?.token || storedTokens.token;
    if (tokenToUse && !isTokenExpired(tokenToUse)) {
      const maxAge = 60 * 60; // 1 hour
      document.cookie = `${TOKEN_NAME}=${tokenToUse}; path=/; secure; samesite=strict; max-age=${maxAge}`;
      console.log('🔐 Access token cookie restored for middleware');
    } else {
      console.warn('⚠️ No valid token to restore, clearing native tokens');
      await clearNativeTokens(websiteKey);
      return;
    }

    // STEP 3: sync GraphLogin (background)
    const graphLogin = getGraphLogin(websiteKey, locale);
    const isReady = await waitForGraphLoginReady(graphLogin);
    if (isReady) {
      const finalTokens = newTokens || storedTokens;
      await graphLogin.saveToken({
        token: finalTokens.token || '',
        refreshToken: finalTokens.refreshToken || '',
      });
      console.log('🔐 GraphLogin synchronized with latest tokens');

      // STEP 4: Close any open GraphLogin modal (handles the case where the app was
      // redirected to /login due to expired token before the sync completed)
      graphLogin.forceCloseModal();
      console.log('🔐 Closed GraphLogin modal if it was open');
    } else {
      console.warn('⚠️ GraphLogin not ready; cookie already set for middleware');
    }
  } catch (error) {
    console.error('🔐 Fatal error in syncTokensWithGraphLogin:', error);
    // Clean up tokens in case of fatal error
    await clearNativeTokens(websiteKey).catch(() => {
      // Ignore errors during cleanup
    });
  }
}
